/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Frontend SPA Contract API
 * Canonical API contract entrypoint.
 *
 * Source architecture:
 * - split by module / bounded context
 * - one file per aggregate root
 * - child entities remain owned by aggregate root
 * - cross-cutting technical concerns live under `openapi/common`
 *
 * OpenAPI spec version: 1.0.0
 */
import type {
  AddExpedientResponsibleBody,
  ChangeExpedientCustomerBody,
  ChangeExpedientRepresentativeBody,
  CreateExpedientRequest,
  ExpedientDto,
  ListExpedientsParams,
  NoteId,
  NoteInput,
  OfferOpeningAcknowledgement,
  OpenCustomerOfferRequest,
  OpenExpedientCustomerOfferFromTemplateBody,
  PaginatedExpedientDto,
  UpdateExpedientRequest,
} from "../model";

import {customFetch} from "../../../runtime/custom-fetch";

export const getListExpedientsUrl = (params?: ListExpedientsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    const explodeParameters = ["countryCodes"];

    if (Array.isArray(value) && explodeParameters.includes(key)) {
      value.forEach((v) => {
        normalizedParams.append(key, v === null ? "null" : String(v));
      });
      return;
    }

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v1/sales/expedients?${stringifiedParams}`
    : `/api/v1/sales/expedients`;
};

/**
 * Filters combine by AND; countryCodes values combine by OR. Absent state includes all states. Literal case-insensitive q searches name, alias, number. Sort defaults name ascending, with ascending UUID tie-break.
 * @summary List expedients
 */
export const listExpedients = async (
  params?: ListExpedientsParams,
  options?: Parameters<typeof customFetch>[1],
): Promise<PaginatedExpedientDto> => {
  return customFetch<PaginatedExpedientDto>(getListExpedientsUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getCreateExpedientUrl = () => {
  return `/api/v1/sales/expedients`;
};

/**
 * Requires eligible customer/contact and optional Representative selection. Backend reserves YY.NNN and initializes responsibles with authenticated creator. No caller identity, number, Notes, responsible set or counter.
 * @summary Create expedient
 */
export const createExpedient = async (
  createExpedientRequest: CreateExpedientRequest,
  options?: Parameters<typeof customFetch>[1],
): Promise<string> => {
  return customFetch<string>(getCreateExpedientUrl(), {
    ...options,
    method: "POST",
    headers: {"Content-Type": "application/json", ...options?.headers},
    body: JSON.stringify(createExpedientRequest),
  });
};

export const getGetExpedientUrl = (id: string) => {
  return `/api/v1/sales/expedients/${id}`;
};

/**
 * @summary Get expedient
 */
export const getExpedient = async (
  id: string,
  options?: Parameters<typeof customFetch>[1],
): Promise<ExpedientDto> => {
  return customFetch<ExpedientDto>(getGetExpedientUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getUpdateExpedientUrl = (id: string) => {
  return `/api/v1/sales/expedients/${id}`;
};

/**
 * ACTIVE only. Full replacement; omitted/null optional groups clear. Retains parties, responsibles, Notes, identity, number and chain counter.
 * @summary Replace expedient descriptive and technical data
 */
export const updateExpedient = async (
  id: string,
  updateExpedientRequest: UpdateExpedientRequest,
  options?: Parameters<typeof customFetch>[1],
): Promise<void> => {
  return customFetch<void>(getUpdateExpedientUrl(id), {
    ...options,
    method: "PUT",
    headers: {"Content-Type": "application/json", ...options?.headers},
    body: JSON.stringify(updateExpedientRequest),
  });
};

export const getDeleteExpedientUrl = (id: string) => {
  return `/api/v1/sales/expedients/${id}`;
};

/**
 * ACTIVE only; related Offer statuses/history remain unchanged.
 * @summary Soft-delete expedient
 */
export const deleteExpedient = async (
  id: string,
  options?: Parameters<typeof customFetch>[1],
): Promise<void> => {
  return customFetch<void>(getDeleteExpedientUrl(id), {
    ...options,
    method: "DELETE",
  });
};

export const getRestoreExpedientUrl = (id: string) => {
  return `/api/v1/sales/expedients/${id}/restore`;
};

/**
 * DELETED or CANCELED to ACTIVE, retaining preserved history.
 * @summary Restore expedient
 */
export const restoreExpedient = async (
  id: string,
  options?: Parameters<typeof customFetch>[1],
): Promise<void> => {
  return customFetch<void>(getRestoreExpedientUrl(id), {
    ...options,
    method: "POST",
  });
};

export const getCancelExpedientUrl = (id: string) => {
  return `/api/v1/sales/expedients/${id}/cancel`;
};

/**
 * ACTIVE to CANCELED; Offer mutation/creation freezes.
 * @summary Cancel expedient
 */
export const cancelExpedient = async (
  id: string,
  options?: Parameters<typeof customFetch>[1],
): Promise<void> => {
  return customFetch<void>(getCancelExpedientUrl(id), {
    ...options,
    method: "POST",
  });
};

export const getChangeExpedientCustomerUrl = (id: string) => {
  return `/api/v1/sales/expedients/${id}/customer`;
};

/**
 * ACTIVE only; existing Offers retain captured selection.
 * @summary Change customer selection
 */
export const changeExpedientCustomer = async (
  id: string,
  changeExpedientCustomerBody: ChangeExpedientCustomerBody,
  options?: Parameters<typeof customFetch>[1],
): Promise<void> => {
  return customFetch<void>(getChangeExpedientCustomerUrl(id), {
    ...options,
    method: "PUT",
    headers: {"Content-Type": "application/json", ...options?.headers},
    body: JSON.stringify(changeExpedientCustomerBody),
  });
};

export const getChangeExpedientRepresentativeUrl = (id: string) => {
  return `/api/v1/sales/expedients/${id}/representative`;
};

/**
 * ACTIVE only; new selection must be ACTIVE/EFFECTIVE with owned Contact and optional Commission. Existing Offers retain captured selection.
 * @summary Replace or clear Representative selection
 */
export const changeExpedientRepresentative = async (
  id: string,
  changeExpedientRepresentativeBody: ChangeExpedientRepresentativeBody,
  options?: Parameters<typeof customFetch>[1],
): Promise<void> => {
  return customFetch<void>(getChangeExpedientRepresentativeUrl(id), {
    ...options,
    method: "PUT",
    headers: {"Content-Type": "application/json", ...options?.headers},
    body: JSON.stringify(changeExpedientRepresentativeBody),
  });
};

export const getAddExpedientResponsibleUrl = (id: string) => {
  return `/api/v1/sales/expedients/${id}/responsibles`;
};

/**
 * ACTIVE dossier/user required; membership grants no permission.
 * @summary Add responsible user
 */
export const addExpedientResponsible = async (
  id: string,
  addExpedientResponsibleBody: AddExpedientResponsibleBody,
  options?: Parameters<typeof customFetch>[1],
): Promise<void> => {
  return customFetch<void>(getAddExpedientResponsibleUrl(id), {
    ...options,
    method: "POST",
    headers: {"Content-Type": "application/json", ...options?.headers},
    body: JSON.stringify(addExpedientResponsibleBody),
  });
};

export const getRemoveExpedientResponsibleUrl = (id: string, responsibleUserId: string) => {
  return `/api/v1/sales/expedients/${id}/responsibles/${responsibleUserId}`;
};

/**
 * ACTIVE only; the requested user must be an assigned responsible. Reject absent membership without changing the set. Never remove the last member; the creator is otherwise removable.
 * @summary Remove responsible user
 */
export const removeExpedientResponsible = async (
  id: string,
  responsibleUserId: string,
  options?: Parameters<typeof customFetch>[1],
): Promise<void> => {
  return customFetch<void>(getRemoveExpedientResponsibleUrl(id, responsibleUserId), {
    ...options,
    method: "DELETE",
  });
};

export const getAddExpedientNoteUrl = (id: string) => {
  return `/api/v1/sales/expedients/${id}/notes`;
};

/**
 * ACTIVE only; backend assigns Note identity.
 * @summary Add expedient Note
 */
export const addExpedientNote = async (
  id: string,
  noteInput: NoteInput,
  options?: Parameters<typeof customFetch>[1],
): Promise<NoteId> => {
  return customFetch<NoteId>(getAddExpedientNoteUrl(id), {
    ...options,
    method: "POST",
    headers: {"Content-Type": "application/json", ...options?.headers},
    body: JSON.stringify(noteInput),
  });
};

export const getEditExpedientNoteUrl = (id: string, noteId: NoteId) => {
  return `/api/v1/sales/expedients/${id}/notes/${noteId}`;
};

/**
 * ACTIVE only; preserve Note identity.
 * @summary Replace expedient Note
 */
export const editExpedientNote = async (
  id: string,
  noteId: NoteId,
  noteInput: NoteInput,
  options?: Parameters<typeof customFetch>[1],
): Promise<void> => {
  return customFetch<void>(getEditExpedientNoteUrl(id, noteId), {
    ...options,
    method: "PUT",
    headers: {"Content-Type": "application/json", ...options?.headers},
    body: JSON.stringify(noteInput),
  });
};

export const getRemoveExpedientNoteUrl = (id: string, noteId: NoteId) => {
  return `/api/v1/sales/expedients/${id}/notes/${noteId}`;
};

/**
 * ACTIVE only.
 * @summary Remove expedient Note
 */
export const removeExpedientNote = async (
  id: string,
  noteId: NoteId,
  options?: Parameters<typeof customFetch>[1],
): Promise<void> => {
  return customFetch<void>(getRemoveExpedientNoteUrl(id, noteId), {
    ...options,
    method: "DELETE",
  });
};

export const getOpenExpedientCustomerOfferUrl = (id: string) => {
  return `/api/v1/sales/expedients/${id}/offers`;
};

/**
 * ACTIVE dossier/eligible captured parties required. Records original authenticated opener, allocated ordinal and one internal server-generated Offer UUID. Repeated internal delivery retains that UUID without creating another revision or overwriting later edits. DRAFT may retain unresolved input. Authored Chapters contain explicit commission applicability and no coefficient; private Customer pricing decisions and review/readiness attribution start unset. New references and protected financial creation must be authorized. Every sold catalog item requires an actual description in the input language even with null quantity or empty supplier choices; otherwise return sales.customerOffer.invalidSelection with located SOURCE_MISSING and do not open a chain. No name or other-language fallback is allowed.
 * @summary Open independent Offer chain
 */
export const openExpedientCustomerOffer = async (
  id: string,
  openCustomerOfferRequest: OpenCustomerOfferRequest,
  options?: Parameters<typeof customFetch>[1],
): Promise<OfferOpeningAcknowledgement> => {
  return customFetch<OfferOpeningAcknowledgement>(getOpenExpedientCustomerOfferUrl(id), {
    ...options,
    method: "POST",
    headers: {"Content-Type": "application/json", ...options?.headers},
    body: JSON.stringify(openCustomerOfferRequest),
  });
};

export const getOpenExpedientCustomerOfferFromTemplateUrl = (id: string) => {
  return `/api/v1/sales/expedients/${id}/offers/from-template`;
};

/**
 * ACTIVE dossier/Template required. Copy language, currency, structure and remembered values; no Template Notes or coefficient decisions. Preserve structural optionality and explicit applyCommission, including false, independently of remembering. Start with unset Customer review/readiness attribution. Bind creator to opener; no later Template propagation. The committed opening retains one internal server-generated Offer UUID across repeated internal delivery without duplicate creation. Protected financial copying requires permission. Opening is new catalog admission: every copied sold item requires a description in the copied language. Missing descriptions, including retained invalid Template selections after a language change, return sales.customerOffer.invalidSelection with located SOURCE_MISSING. Preserve the source Template and create no chain or revision. Customer successor copying retains its separate repairable-reference policy.
 * @summary Open independent Offer from Template
 */
export const openExpedientCustomerOfferFromTemplate = async (
  id: string,
  openExpedientCustomerOfferFromTemplateBody: OpenExpedientCustomerOfferFromTemplateBody,
  options?: Parameters<typeof customFetch>[1],
): Promise<OfferOpeningAcknowledgement> => {
  return customFetch<OfferOpeningAcknowledgement>(
    getOpenExpedientCustomerOfferFromTemplateUrl(id),
    {
      ...options,
      method: "POST",
      headers: {"Content-Type": "application/json", ...options?.headers},
      body: JSON.stringify(openExpedientCustomerOfferFromTemplateBody),
    },
  );
};
